import React, { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../theme';
import type { ButtonProps } from './Button.types';

/**
 * Button variants using class-variance-authority
 */
export const buttonVariants = cva(
  // Base styles
  [
    'inline-flex items-center justify-center gap-2',
    'font-semibold rounded-md',
    'transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-gradient-to-br from-sage to-sage-dark text-white',
          'shadow-soft hover:shadow-medium',
          'hover:-translate-y-0.5',
          'focus-visible:ring-sage',
        ],
        secondary: [
          'bg-white text-sage border-2 border-warm-gray/20',
          'hover:border-sage hover:bg-cream',
          'focus-visible:ring-sage',
        ],
        ghost: [
          'text-navy hover:bg-sage/10 hover:text-sage-dark',
          'focus-visible:ring-sage',
        ],
        danger: [
          'bg-gradient-to-br from-red-500 to-red-600 text-white',
          'shadow-soft hover:shadow-medium',
          'hover:-translate-y-0.5',
          'focus-visible:ring-red-500',
        ],
        outline: [
          'border-2 border-sage text-sage',
          'hover:bg-sage hover:text-white',
          'focus-visible:ring-sage',
        ],
        accent: [
          'bg-gradient-to-br from-terracotta to-terracotta-light text-white',
          'shadow-soft hover:shadow-medium',
          'hover:-translate-y-0.5',
          'focus-visible:ring-terracotta',
        ],
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-7 text-base',
        lg: 'h-13 px-8 text-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      iconOnly: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Icon-only sizing
      { iconOnly: true, size: 'sm', className: 'w-9 px-0' },
      { iconOnly: true, size: 'md', className: 'w-11 px-0' },
      { iconOnly: true, size: 'lg', className: 'w-13 px-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
      iconOnly: false,
    },
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

/**
 * Button component following the BrightPath Design System.
 * 
 * A versatile button component with multiple variants, sizes, and states.
 * Supports icons, loading states, and can be rendered as any element using asChild.
 * 
 * @example
 * // Primary button
 * <Button>Click me</Button>
 * 
 * // With icon
 * <Button leftIcon={<Plus />}>Add Item</Button>
 * 
 * // Loading state
 * <Button loading>Saving...</Button>
 * 
 * // As link
 * <Button asChild>
 *   <a href="/dashboard">Go to Dashboard</a>
 * </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      iconOnly = false,
      disabled,
      children,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    // Determine icon sizes based on button size
    const iconSizeClass = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-5 w-5',
    }[size];

    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({ variant, size, fullWidth, iconOnly }),
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className={cn(iconSizeClass, 'animate-spin')} />
            {!iconOnly && children}
          </>
        ) : (
          <>
            {leftIcon && (
              <span className={cn(iconSizeClass, 'shrink-0')}>
                {leftIcon}
              </span>
            )}
            {!iconOnly && children}
            {rightIcon && (
              <span className={cn(iconSizeClass, 'shrink-0')}>
                {rightIcon}
              </span>
            )}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export default Button;
